import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { format } from 'date-fns';
import {
  DownloadIcon,
  EyeIcon,
  FilesIcon,
  PencilIcon,
  TriangleAlertIcon
} from 'lucide-react';
import { type ReactNode, useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import ReportEditorDialog from './RightSidebar/ReportEditorDialog';
import useOutletContext from './useOutletContext';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { PdfViewerDialog } from '@/components/reports/PdfViewerDialog';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface GenerateDisReport {
  generateDisReport: {
    data: null | string;
    message: null | string;
    ok: boolean | null;
  };
}

const GENERATE_DIS_REPORT = gql`
  mutation GenerateDisReport($id: ID!) {
    generateDisReport(opportunityId: $id) {
      ok
      data
      message
    }
  }
`;

interface GenerateRsmReport {
  generateRsmReport: {
    data: null | string;
    message: null | string;
    ok: boolean | null;
  };
}

const GENERATE_RSM_REPORT = gql`
  mutation GenerateRSM($id: ID!) {
    generateRsmReport(opportunityId: $id) {
      ok
      data
      message
    }
  }
`;

interface GenerateIcbReport {
  generateIcbReport: {
    data: null | string;
    message: null | string;
    ok: boolean | null;
  };
}

const GENERATE_ICB_REPORT = gql`
  mutation GenerateIcbReport($id: ID!) {
    generateIcbReport(opportunityId: $id) {
      ok
      data
      message
    }
  }
`;

interface GenerateIcmReport {
  generateIcmReport: {
    data: null | string;
    message: null | string;
    ok: boolean | null;
  };
}

const GENERATE_ICM_REPORT = gql`
  mutation GenerateICMReport($id: ID!) {
    generateIcmReport(opportunityId: $id) {
      ok
      data
      message
    }
  }
`;

interface GenerateFicmReport {
  generateFicmReport: {
    data: null | string;
    message: null | string;
    ok: boolean | null;
  };
}

const GENERATE_FICM_REPORT = gql`
  mutation GenerateFICMReport($id: ID!) {
    generateFicmReport(opportunityId: $id) {
      ok
      data
      message
    }
  }
`;

const REPORTS = [
  {
    filenameSuffix: 'DIS',
    name: 'Direct Investment Screening'
  },
  {
    filenameSuffix: 'RSM',
    name: 'Refined Screening Memo'
  },
  {
    filenameSuffix: 'ICB',
    name: 'Investment Committee Briefing'
  },
  {
    filenameSuffix: 'ICM',
    name: 'Investment Committee Memo'
  },
  {
    filenameSuffix: 'FICM',
    name: 'Final Investment Committee Memo'
  }
] as const;

const REPORT_KEYS_BY_INDEX = ['dis', 'rsm', 'icb', 'icm', 'ficm'] as const;

interface Props {
  children?: ReactNode;
}

const Header = ({ children }: Props) => {
  const { companyName, ddStage, id, opportunityName, projectName } =
    useOutletContext();
  const navigate = useNavigate();
  const [isReportsDialogOpen, setIsReportsDialogOpen] = useState(false);
  const [generateDisReport, { loading: isGeneratingDisReport }] = useMutation<
    GenerateDisReport,
    { id: string }
  >(GENERATE_DIS_REPORT);

  const [generateRsmReport, { loading: isGeneratingRsmReport }] = useMutation<
    GenerateRsmReport,
    { id: string }
  >(GENERATE_RSM_REPORT);

  const [generateIcbReport, { loading: isGeneratingIcbReport }] = useMutation<
    GenerateIcbReport,
    { id: string }
  >(GENERATE_ICB_REPORT);

  const [generateIcmReport, { loading: isGeneratingIcmReport }] = useMutation<
    GenerateIcmReport,
    { id: string }
  >(GENERATE_ICM_REPORT);

  const [generateFicmReport, { loading: isGeneratingFicmReport }] = useMutation<
    GenerateFicmReport,
    { id: string }
  >(GENERATE_FICM_REPORT);

  const [clickedButtonType, setClickedButtonType] = useState<
    'download' | 'edit' | 'view'
  >();

  const [reportEditorState, setReportEditorState] = useState<{
    fileData: null | string;
    filename: string;
    open: boolean;
    reportIndex: null | number;
  }>({ fileData: null, filename: '', open: false, reportIndex: null });

  const [reportViewerState, setReportViewerState] = useState<{
    fileData: null | string;
    filename: string;
    open: boolean;
  }>({ fileData: null, filename: '', open: false });

  const downloadBase64File = useCallback(
    (base64String: string, filename: string) => {
      const match = /^data:([^;]+);base64,(.*)$/.exec(base64String.trim());

      const base64 = (match?.[2] ?? base64String).replace(/\s/g, '');

      const byteCharacters = atob(base64);
      const byteNumbers = new Array<number>(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: 'application/pdf;base64'
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    []
  );

  const getReportFilename = useCallback(
    (index: number) => {
      const filenamePrefix =
        typeof opportunityName === 'string' && opportunityName.trim()
          ? opportunityName
          : (projectName ?? '');

      return `${filenamePrefix}-${REPORTS[index].filenameSuffix}-${format(new Date(), 'MM-dd-yyyy')}.pdf`;
    },
    [opportunityName, projectName]
  );

  const generateReport = useCallback(
    async (index: number) => {
      if (index === 0) {
        const { data, error } = await generateDisReport({
          variables: { id }
        });

        if (error) {
          throw new Error('An error has occurred');
        }

        return data?.generateDisReport.data ?? '';
      }

      if (index === 1) {
        const { data, error } = await generateRsmReport({
          variables: { id }
        });

        if (error) {
          throw new Error('An error has occurred');
        }

        return data?.generateRsmReport.data ?? '';
      }

      if (index === 2) {
        const { data, error } = await generateIcbReport({
          variables: { id }
        });

        if (error) {
          throw new Error('An error has occurred');
        }

        return data?.generateIcbReport.data ?? '';
      }

      if (index === 3) {
        const { data, error } = await generateIcmReport({
          variables: { id }
        });

        if (error) {
          throw new Error('An error has occurred');
        }

        return data?.generateIcmReport.data ?? '';
      }

      if (index === 4) {
        const { data, error } = await generateFicmReport({
          variables: { id }
        });

        if (error) {
          throw new Error('An error has occurred');
        }

        return data?.generateFicmReport.data ?? '';
      }

      return '';
    },
    [
      generateDisReport,
      generateFicmReport,
      generateIcbReport,
      generateIcmReport,
      generateRsmReport,
      id
    ]
  );

  const handleViewButtonClick = useCallback(
    async (index: number) => {
      setClickedButtonType('view');

      try {
        const base64File = await generateReport(index);

        if (!base64File.trim()) {
          toast.error('Report is not available.');
          return;
        }

        setReportViewerState({
          fileData: base64File,
          filename: getReportFilename(index),
          open: true
        });
      } catch (error_) {
        const error = error_ as Error;
        console.error(error);
        toast.error(error.message);
      }

      setClickedButtonType(undefined);
    },
    [generateReport, getReportFilename]
  );

  const handleDownloadButtonClick = useCallback(
    async (index: number) => {
      setClickedButtonType('download');

      try {
        const base64File = await generateReport(index);
        downloadBase64File(base64File, getReportFilename(index));
      } catch (error_) {
        const error = error_ as Error;
        console.error(error);
        toast.error(error.message);
      }

      setClickedButtonType(undefined);
    },
    [downloadBase64File, generateReport, getReportFilename]
  );

  const handleEditButtonClick = useCallback(
    (index: number) => {
      setIsReportsDialogOpen(false);
      void navigate(`/investments/direct/${id}/reports`, {
        state: {
          initialReportKey: REPORT_KEYS_BY_INDEX[index],
          opportunityId: id,
          projectName
        }
      });
    },
    [id, navigate, projectName]
  );

  const handleRefetchOpenReport = useCallback(async () => {
    const index = reportEditorState.reportIndex;
    if (index == null) return;
    try {
      const base64File = await generateReport(index);
      setReportEditorState(prev => ({ ...prev, fileData: base64File }));
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  }, [generateReport, reportEditorState.reportIndex]);

  const isGeneratingByIndex = [
    isGeneratingDisReport,
    isGeneratingRsmReport,
    isGeneratingIcbReport,
    isGeneratingIcmReport,
    isGeneratingFicmReport
  ];

  const getWarningStartIndex = useCallback((ddStage: null | string) => {
    const normalizedDdstage = ddStage
      ?.toLowerCase()
      .replace(/[\s_-]+/g, ' ')
      .trim();

    if (!normalizedDdstage) {
      return null;
    }

    if (normalizedDdstage === 'screening') {
      return 0;
    }

    if (
      normalizedDdstage === 'refined screening' ||
      normalizedDdstage === 'ioi' ||
      normalizedDdstage === 'icb'
    ) {
      return 1;
    }

    if (normalizedDdstage === 'term sheet') {
      return 2;
    }

    if (normalizedDdstage === 'icm') {
      return 3;
    }

    if (normalizedDdstage === 'ficm') {
      return 4;
    }

    return null;
  }, []);

  const warningStartIndex = getWarningStartIndex(ddStage ?? null);

  return (
    <>
      <div className="bg-sidebar sticky top-0 z-1 flex h-12.25 items-center justify-between gap-x-5 border-b px-5 py-1.5">
        <div className="flex min-w-0 grow items-center gap-x-5">
          <TruncatedTextTooltip className="max-w-1/2 text-lg font-semibold">
            {projectName}
          </TruncatedTextTooltip>
          <TruncatedTextTooltip className="text-muted-foreground max-w-1/2 text-sm font-medium">
            {companyName ?? '-'}
          </TruncatedTextTooltip>
        </div>
        <div className="flex items-center gap-2.5">
          {children}
          <Button
            onClick={() => {
              setIsReportsDialogOpen(true);
            }}
            variant="outline"
          >
            <FilesIcon />
            Reports
          </Button>
        </div>
      </div>
      <Dialog
        onOpenChange={setIsReportsDialogOpen}
        open={isReportsDialogOpen}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Reports</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-2.5 pt-3">
              {REPORTS.map((report, index) => (
                <div
                  className="flex items-center justify-between border-b pb-2.5"
                  key={report.name}
                >
                  <div className="flex items-center gap-x-1">
                    <div className="rounded-sm bg-teal-50 p-2 text-teal-900">
                      <FilesIcon />
                    </div>
                    <div className="text-sm font-medium">{report.name}</div>
                  </div>
                  <div className="flex items-center gap-x-1">
                    {warningStartIndex !== null &&
                      index >= warningStartIndex && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              aria-label="Report stage warning"
                              size="icon"
                              type="button"
                              variant="ghost"
                            >
                              <TriangleAlertIcon className="text-destructive" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-80">
                            <p>
                              Be careful, you are currently in {ddStage}, this
                              memo may be empty or partially completed. Visit{' '}
                              <Link
                                className="underline"
                                to={`/investments/direct/${id}/due-diligence`}
                              >
                                this link
                              </Link>{' '}
                              for more details.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    <Button
                      disabled={clickedButtonType !== undefined}
                      onClick={() => {
                        handleEditButtonClick(index);
                      }}
                      size="icon"
                      variant="outline"
                    >
                      {clickedButtonType === 'edit' &&
                      isGeneratingByIndex[index] ? (
                        <Spinner />
                      ) : (
                        <PencilIcon />
                      )}
                    </Button>
                    <Button
                      disabled={clickedButtonType !== undefined}
                      onClick={() => void handleViewButtonClick(index)}
                      size="icon"
                      variant="outline"
                    >
                      {clickedButtonType === 'view' &&
                      isGeneratingByIndex[index] ? (
                        <Spinner />
                      ) : (
                        <EyeIcon />
                      )}
                    </Button>
                    <Button
                      disabled={clickedButtonType !== undefined}
                      onClick={() => void handleDownloadButtonClick(index)}
                      size="icon"
                    >
                      {clickedButtonType === 'download' &&
                      isGeneratingByIndex[index] ? (
                        <Spinner />
                      ) : (
                        <DownloadIcon />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
      <ReportEditorDialog
        fileData={reportEditorState.fileData}
        filename={reportEditorState.filename}
        onOpenChange={open => {
          setReportEditorState(prev => ({ ...prev, open }));
        }}
        onRefetchReport={handleRefetchOpenReport}
        open={reportEditorState.open}
        opportunityId={id}
      />
      <PdfViewerDialog
        filename={reportViewerState.filename}
        onOpenChange={open => {
          setReportViewerState(prev => ({
            ...prev,
            open,
            ...(!open && { fileData: null, filename: '' })
          }));
        }}
        open={reportViewerState.open}
        pdfData={reportViewerState.fileData}
      />
    </>
  );
};

export default Header;
